/**
 * WorkspaceShell — the per-page content host inside the persistent chrome.
 *
 * Each `(workspace)/*` route renders `<WorkspaceShell breadcrumbs={…}>` and
 * puts its own page content inside. The Sidebar + Topbar themselves live in
 * the `(workspace)` layout route (`routes/(workspace)/route.tsx`) and mount
 * once for the session — so navigating between pages no longer tears the
 * chrome down and rebuilds it (the old flicker). This component now does two
 * things:
 *
 *   1. Publishes the page's breadcrumbs / bell count / optional org override
 *      up to the persistent chrome via `useWorkspaceChrome` (so each route can
 *      keep declaring its breadcrumbs inline — the pattern the team chose over
 *      threading `useMatches`).
 *   2. Renders the right column's scrollable content region and gives it the
 *      `animate-page-in` fade+rise, which plays on each navigation because the
 *      shell remounts with the outlet while the chrome stays put.
 *
 * Layout contract: the shell is locked to viewport height
 * (`h-screen overflow-hidden`) by the layout route. The content region here
 * is the only scrollable surface in the right column (the topbar is pinned by
 * the layout). `disableViewportScroll` hands the route a plain
 * `flex-1 min-h-0 overflow-hidden` block when it owns its own scroll regions.
 *
 * Spec: prototype/sales/customers.html top-level shell — `.shell` grid + sidebar.
 */
import * as React from "react";
import { cn, ScrollArea } from "@flint/ui";
import { useWorkspaceChrome, type ChromeSectionTab } from "../lib/use-workspace-chrome.ts";
import type { BreadcrumbItem } from "./breadcrumbs.tsx";

export interface WorkspaceShellProps {
  /**
   * Breadcrumb trail for the topbar. Routes pass `[{ label: 'Sales', to:
   * '/' }, { label: 'Customers' }]` etc. The last item is the active leaf.
   *
   * Convenience: a single-item array is fine for top-level pages —
   * `[{ label: 'Workspace home' }]`.
   */
  breadcrumbs: BreadcrumbItem[];
  /**
   * Number of unread notifications — drives the topbar bell ping dot.
   * Routes that have richer per-page knowledge can override; otherwise
   * this defaults to 0 until the notifications module wires a real
   * unread query at the topbar level.
   */
  unreadNotifications?: number;
  /** Overrides the right-column scroll viewport spacing for route-specific surfaces. */
  viewportClassName?: string;
  /** Overrides the inner page container for full-bleed route surfaces. */
  contentClassName?: string;
  /**
   * Opt out of the workspace's ScrollArea wrapper for routes that
   * manage their own scroll regions (e.g. the Ask Flint chat surface
   * where the transcript scrolls independently of the composer).
   * The right column instead becomes a plain `flex-1` block — the
   * route is responsible for keeping its content inside the available
   * height (use `h-full min-h-0 overflow-hidden` on the root).
   */
  disableViewportScroll?: boolean;
  /**
   * When true, the layout skips rendering the SectionTabBar. Used by
   * full-viewport authoring routes (e.g. `/reports/new`) where the
   * section tabs would be redundant against the topbar breadcrumbs.
   */
  hideSectionTabs?: boolean;
  /**
   * When true, the `(workspace)` layout renders no Topbar at all. For modules
   * that own their full chrome (the Store admin's left rail carries its own
   * brand header and account footer). Leave it off everywhere else.
   */
  hideTopbar?: boolean;
  /**
   * Page-owned tabs rendered in the Topbar in place of the nav-config group
   * tabs. For pages whose sections are filters on one route (e.g. the Inbox
   * status tabs) rather than sibling routes.
   */
  sectionTabs?: ChromeSectionTab[];
  children: React.ReactNode;
}

export function WorkspaceShell({
  breadcrumbs,
  unreadNotifications,
  viewportClassName,
  contentClassName,
  disableViewportScroll = false,
  hideSectionTabs = false,
  hideTopbar = false,
  sectionTabs,
  children,
}: WorkspaceShellProps) {
  // Publish this page's chrome up to the persistent Sidebar/Topbar (which
  // live in the `(workspace)` layout route). A layout effect runs before
  // paint, so the topbar shows the new breadcrumbs in the same frame the
  // page appears — no stale-breadcrumb flash. `breadcrumbsKey` keeps the
  // effect from re-firing on every render for the same trail (the route
  // passes a fresh array literal each time).
  const breadcrumbsKey = breadcrumbs.map((b) => `${b.label}\u001f${b.to ?? ""}`).join("|");
  const sectionTabsKey = sectionTabs
    ? sectionTabs
        .map((t) => [t.label, t.to, JSON.stringify(t.search ?? {}), t.active, t.count ?? 0].join(" "))
        .join("|")
    : null;
  React.useLayoutEffect(() => {
    useWorkspaceChrome.getState().setChrome({
      breadcrumbs,
      unreadNotifications: unreadNotifications ?? 0,
      hideSectionTabs,
      hideTopbar,
      sectionTabs: sectionTabs ?? null,
    });
    // `breadcrumbs`/`sectionTabs` are read fresh inside the effect; the
    // `*Key` strings are their stable identities for the dependency check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbsKey, sectionTabsKey, unreadNotifications, hideSectionTabs, hideTopbar]);

  // `animate-page-in` (styles/index.css) gives a subtle fade + 6px rise on
  // mount. The shell remounts with the outlet on each navigation while the
  // chrome stays put, so this plays per page without an exit animation —
  // the transition the design partner asked for, flicker-free.
  const content = (
    <div className={cn("animate-page-in mx-auto w-full max-w-[1280px]", contentClassName)}>
      {children}
    </div>
  );

  return disableViewportScroll ? (
    // Route owns scroll — Radix ScrollArea would otherwise wrap children in
    // its own overflow container which always wins over content-level
    // overflow classes. We hand the route a plain
    // `flex-1 min-h-0 overflow-hidden` shell so its internal regions
    // (transcript / sidebar list) can scroll independently.
    <div className={cn("flex flex-1 min-h-0 overflow-hidden", viewportClassName)}>{content}</div>
  ) : (
    // The default scroll surface for the right column. `min-h-0` on the flex
    // parent + `flex-1` here is the standard "child of fixed-height flex
    // column scrolls properly" idiom.
    // `[&>div]:!block` overrides Radix ScrollArea's inner `display:table` wrapper,
    // which otherwise shrink-wraps to the widest descendant — on mobile a single wide
    // child (a long action row, a min-width table) dragged the whole column (header
    // included) off-screen with no page scroll. Block keeps the column at viewport
    // width so headers wrap and wide children self-scroll. (QA finding F-UX1.)
    <ScrollArea className="flex-1" viewportClassName={cn("p-5 md:p-6 [&>div]:!block", viewportClassName)}>
      {content}
    </ScrollArea>
  );
}
